import {
  ArrowsLeftRight, ArrowUUpLeft, Buildings, CheckCircle, Clock, Flag, GridFour, HandArrowDown, Handshake, MapPin, Motorcycle, Package, Cube, PhoneX, Prohibit, Storefront,
  Truck, Warning, XCircle, Tag,
} from '@phosphor-icons/react'
import { shipmentTypeGlyph, shipmentTypeKey } from '@/design-system/components/ShipmentTypeIcon'
import type { IconComponent } from '@/design-system'
import type { LookupKey } from './lookupService'

/**
 * One SVG per lookup kind, so a select's meaning is legible at a glance instead of relying on
 * a text-only value (statuses, cities, shipment types, ... all read the same until you read them).
 */
export const LOOKUP_ICONS: Record<LookupKey, IconComponent> = {
  statuses: Flag,
  statusCatalogue: Flag,
  deliveryStatuses: CheckCircle,
  deliveryTypes: Cube,
  pickupStatuses: HandArrowDown,
  reasons: Warning,
  mainReasons: Warning,
  clients: Buildings,
  clientsAll: Buildings,
  im2Clients: Buildings,
  cities: MapPin,
  cityCodes: MapPin,
  branches: Buildings,
  runners: Truck,
  cells: GridFour,
  shipmentTypes: Package,
  users: Buildings,
  callTypes: Flag,
  callStatuses: Flag,
  callResults: CheckCircle,
  csTypes: Flag,
  csStatuses: Flag,
  transactionTypes: Package,
  auditCategories: Flag,
  im2Stores: Buildings,
  recipients: Buildings,
  priceLists: Tag,
  products: Cube,
  accountTypes: Buildings,
}

/** The lookup a field icon stands for — lets a Select pick per-option icons without every caller changing. */
export function lookupOfIcon(icon: IconComponent | undefined): LookupKey | undefined {
  if (!icon) return undefined
  return (Object.keys(LOOKUP_ICONS) as LookupKey[]).find((k) => LOOKUP_ICONS[k] === icon)
}

const has = (label: string, ...words: string[]) => words.some((w) => label.includes(w))

/**
 * A specific icon per option where the label says what it is (delivered vs returned, delivery vs
 * exchange, ...), falling back to the kind's icon. Keyword-based on Arabic + English names because
 * Dispatch's ids differ per company while the words don't.
 */
export function iconForOption(lookup: LookupKey | undefined, label: string): IconComponent | undefined {
  if (!lookup) return undefined
  const l = label.toLowerCase()
  switch (lookup) {
    case 'shipmentTypes':
      // the coloured type marks (same as the create-shipment cards), not a grey generic glyph
      return shipmentTypeGlyph(shipmentTypeKey(label)) as unknown as IconComponent
    case 'statuses':
    case 'statusCatalogue':
    case 'deliveryStatuses':
    case 'deliveryTypes':
      // delivery results / types first: full, partial, exchange, return — the words a dispatcher reads
      if (has(l, 'جزئي', 'جزء', 'partial')) return Cube
      if (has(l, 'استبدال', 'exchange', 'replace')) return ArrowsLeftRight
      if (has(l, 'استرجاع', 'return', 'rts', 'مرتجع')) return ArrowUUpLeft
      if (has(l, 'تم التسليم', 'delivered', 'كامل', 'full')) return CheckCircle
      if (has(l, 'مرتجع', 'return', 'rts')) return ArrowUUpLeft
      if (has(l, 'مؤجل', 'postpon', 'reschedul')) return Clock
      if (has(l, 'ملغ', 'cancel')) return XCircle
      if (has(l, 'رفض', 'refus', 'reject')) return Prohibit
      if (has(l, 'مش بيرد', 'لا يرد', 'no answer', 'unreach')) return PhoneX
      if (has(l, 'مندوب', 'runner', 'out for')) return Motorcycle
      if (has(l, 'فرع', 'branch', 'hub')) return Buildings
      return Flag
    case 'pickupStatuses':
      if (has(l, 'تم', 'done', 'received', 'مستلم')) return CheckCircle
      if (has(l, 'ملغ', 'cancel')) return XCircle
      if (has(l, 'مندوب', 'assign')) return Motorcycle
      return HandArrowDown
    case 'reasons':
    case 'mainReasons':
      if (has(l, 'مش بيرد', 'لا يرد', 'no answer', 'مغلق')) return PhoneX
      if (has(l, 'رفض', 'refus')) return Prohibit
      if (has(l, 'مؤجل', 'تأجيل', 'postpon')) return Clock
      return Warning
    case 'runners':
      return Motorcycle
    case 'clients':
    case 'im2Clients':
      return has(l, 'متجر', 'store', 'shop') ? Storefront : Handshake
    case 'cities':
      return MapPin
    case 'branches':
      return Buildings
    case 'cells':
      return GridFour
  }
}
